import { createAction } from '@reduxjs/toolkit';
import { JobLog } from 'src/models/jobLog';
import { JobLogV2Data } from 'src/utils/logs/types';
import { ErrorPayload } from '../types';
import { LocalJobLogData } from './types';

export interface FetchLogBasePayload {
  /** The id of the log being fetched. */
  id: string;
  /** The timestamp associated with the start of the entire request. Used to correlate actions. */
  timestamp: number;
}

interface FetchLogStartPayload extends FetchLogBasePayload {}
/** An action that is dispatched when the log parser process begins. */
const fetchLogStart = createAction<FetchLogStartPayload>('LOGS.FETCH.START');

interface FetchLogDownloadedPayload extends FetchLogBasePayload {
  /** The number of lines in the log file. */
  totalLines: number;
}
/** An action that is dispatched when the raw log file has been downloaded. */
const fetchLogDownloaded = createAction<FetchLogDownloadedPayload>('LOGS.FETCH.DOWNLOADED');

interface FetchLogParseProgressPayload extends FetchLogBasePayload {
  /** The number of lines that have been parsed so far. */
  parsedLines: number;
}
/** An action that is dispatched when the log parser progress changes. */
const fetchLogParseProgress = createAction<FetchLogParseProgressPayload>('LOGS.FETCH.PARSE_PROGRESS');

interface FetchLogSuccessPayload extends FetchLogBasePayload {
  /** A string which uniquely identifies a specific version of a log file. */
  etag?: string;
  /** The parsed log data. */
  data: JobLogV2Data;
  /** Whether or not there were errors during the parsing process. */
  hasErrors: boolean;
}
/** An action that is dispatched when the log parser completes successfully. */
const fetchLogSuccess = createAction<FetchLogSuccessPayload>('LOGS.FETCH.SUCCESS');

interface FetchLogFailurePayload extends FetchLogBasePayload {
  error?: string;
}
/** An action that is dispatched when the log parser completes unsuccessfully. */
const fetchLogFailure = createAction<FetchLogFailurePayload>('LOGS.FETCH.FAILURE');

interface FetchLogResetPayload extends FetchLogBasePayload {}
/** An action that is dispatched when the data for the provided log id should be cleared. */
const fetchLogReset = createAction<FetchLogResetPayload>('LOGS.FETCH.RESET');

interface UpdateLogInfoPayload extends LiveJobLogBasePayload {
  /** Metadata properties that provide details on the job. */
  metadata?: LocalJobLogData['metadata'];
}
/** An action that is dispatched when the application receives an updated job log from the sentinel system. */
const updateLogInfo = createAction<UpdateLogInfoPayload>('LOGS.UPDATE_INFO');

export interface LiveJobLogBasePayload {
  /** The filename of the log file. */
  filename: string;
}

/** An action that is dispatched when a connected controller initiates a live stream. */
const liveJobLogStreamBegin = createAction<LiveJobLogBasePayload>('LOGS.LIVE_JOB_LOG.STREAM_BEGIN');

interface LiveJobLogOpenPayload extends LiveJobLogBasePayload {
  /** The serial number of the controller that started the log. */
  serialNumber: string;
  /** Whether or not metadata collection should be skipped for this log. */
  skipMetadataCollection: boolean;
}

/** An action that is dispatched when the live job log is opened. */
const liveJobLogOpen = createAction<LiveJobLogOpenPayload>('LOGS.LIVE_JOB_LOG.OPEN');

/** An action that is dispatched when the live job log detects the start of a log file, indicating the log file has begun replaying. */
const liveJobLogReplayDetected = createAction<LiveJobLogBasePayload>('LOGS.LIVE_JOB_LOG.REPLAY_DETECTED');

/** An action that is dispatched when the live job log sends the replay status, indicating we are about to receive a replay of the log file. */
const liveJobLogReplayRequested = createAction<LiveJobLogBasePayload>('LOGS.LIVE_JOB_LOG.REPLAY_REQUESTED');

/** An action that is dispatched when the system begins creating a job log in the SA system. */
const liveJobLogInitializeStart = createAction<LiveJobLogBasePayload>('LOGS.LIVE_JOB_LOG.INIT.START');

interface LiveJobLogInitializeSuccessPayload extends LiveJobLogBasePayload {
  /** The information about the job log in the sentinel system. */
  info: JobLog;
}
/** An action that is dispatched when the system completes creating a job log in the SA system successfully. */
const liveJobLogInitializeSuccess = createAction<LiveJobLogInitializeSuccessPayload>('LOGS.LIVE_JOB_LOG.INIT.SUCCESS');

interface LiveJobLogInitializeFailurePayload extends ErrorPayload, LiveJobLogBasePayload {}
/** An action that is dispatched when the system completes creating a job log in the SA system unsuccessfully. */
const liveJobLogInitializeFailure = createAction<LiveJobLogInitializeFailurePayload>('LOGS.LIVE_JOB_LOG.INIT.FAILURE');

/** An action that is dispatched when the system begins updating the metadata about the log in the sentinel system. */
const liveJobLogUpdateMetadataStart = createAction<LiveJobLogBasePayload>('LOGS.LIVE_JOB_LOG.UPDATE_METADATA.START');

/** An action that is dispatched when the system completes updating the metadata about the log in the sentinel system successfully. */
const liveJobLogUpdateMetadataSuccess = createAction<LiveJobLogBasePayload>(
  'LOGS.LIVE_JOB_LOG.UPDATE_METADATA.SUCCESS',
);

interface LiveJobLogUpdateMetadataFailurePayload extends LiveJobLogBasePayload, ErrorPayload {}
/** An action that is dispatched when the system completes updating the metadata about the log in the sentinel system unsuccessfully. */
const liveJobLogUpdateMetadataFailure = createAction<LiveJobLogUpdateMetadataFailurePayload>(
  'LOGS.LIVE_JOB_LOG.UPDATE_METADATA.FAILURE',
);

/** An action that is dispatched when the system begins syncing a job log in the SA system. */
const liveJobLogSyncStart = createAction<LiveJobLogBasePayload>('LOGS.LIVE_JOB_LOG.SYNC.START');

/** An action that is dispatched when the system completes syncing a job log in the SA system successfully. */
const liveJobLogSyncSuccess = createAction<LiveJobLogBasePayload>('LOGS.LIVE_JOB_LOG.SYNC.SUCCESS');

interface LiveJobLogSyncFailurePayload extends ErrorPayload, LiveJobLogBasePayload {}
/** An action that is dispatched when the system completes syncing a job log in the SA system unsuccessfully. */
const liveJobLogSyncFailure = createAction<LiveJobLogSyncFailurePayload>('LOGS.LIVE_JOB_LOG.SYNC.FAILURE');

/** An action that is dispatched when the system begins finalizing a job log in the SA system. */
const liveJobLogFinalizeStart = createAction<LiveJobLogBasePayload>('LOGS.LIVE_JOB_LOG.FINALIZE.START');

/** An action that is dispatched when the system completes finalizing a job log in the SA system successfully. */
const liveJobLogFinalizeSuccess = createAction<LiveJobLogBasePayload>('LOGS.LIVE_JOB_LOG.FINALIZE.SUCCESS');

interface LiveJobLogFinalizeFailurePayload extends ErrorPayload, LiveJobLogBasePayload {}
/** An action that is dispatched when the system completes finalizing a job log in the SA system unsuccessfully. */
const liveJobLogFinalizeFailure = createAction<LiveJobLogFinalizeFailurePayload>('LOGS.LIVE_JOB_LOG.FINALIZE.FAILURE');

interface LiveJobLogProgressPayload extends LiveJobLogBasePayload {
  /** The next parsed job log data. */
  data: JobLogV2Data;
  /** The data to append. */
  raw: string;
  /** Whether or not the data had an error when parsing. */
  error: boolean;
}
const liveJobLogProgress = createAction<LiveJobLogProgressPayload>('LOGS.LIVE_JOB_LOG.PROGRESS');

interface LiveJobLogClosePayload extends LiveJobLogBasePayload {}
/** An action that is dispatched when the live job is closed. */
const liveJobLogClose = createAction<LiveJobLogClosePayload>('LOGS.LIVE_JOB_LOG.CLOSE');

const tryFetchLogInfoStart = createAction('LOGS.TRY_FETCH_INFO.START');
const tryFetchLogInfoSuccess = createAction<FetchLogSuccessPayload>('LOGS.TRY_FETCH_INFO.SUCCESS');
const tryFetchLogInfoFailure = createAction<FetchLogFailurePayload>('LOGS.TRY_FETCH_INFO.FAILURE');

export const logActions = {
  fetchLogStart,
  fetchLogDownloaded,
  fetchLogParseProgress,
  fetchLogSuccess,
  fetchLogFailure,
  fetchLogReset,
  updateLogInfo,
  liveJobLogStreamBegin,
  liveJobLogOpen,
  liveJobLogReplayDetected,
  liveJobLogReplayRequested,
  liveJobLogInitializeStart,
  liveJobLogInitializeSuccess,
  liveJobLogInitializeFailure,
  liveJobLogUpdateMetadataStart,
  liveJobLogUpdateMetadataSuccess,
  liveJobLogUpdateMetadataFailure,
  liveJobLogSyncStart,
  liveJobLogSyncSuccess,
  liveJobLogSyncFailure,
  liveJobLogFinalizeStart,
  liveJobLogFinalizeSuccess,
  liveJobLogFinalizeFailure,
  liveJobLogProgress,
  liveJobLogClose,
  tryFetchLogInfoStart,
  tryFetchLogInfoSuccess,
  tryFetchLogInfoFailure,
};
